import {
  HabitSchedule,
  PlannedOccurrence,
  ScheduleEntryState,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  addDays,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
} from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { formatDateKey } from '../format-date-key';
import { isBetween } from '../lib/date-fns';
import { getOccurrences } from './get-occurrences';
import { getScheduleEndDate } from './get-schedule-end-date';
import { getScheduleTimeframesByDateRange } from './get-schedule-timeframe-by-date';
import { memoizeByScheduleAndVariant } from './memoize-by-schedule';

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  weekStartsOn: WeekDays;
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
  strictEndDate?: boolean;
};

type RruleEntryIndexOptions = {
  uncompletedOnly?: boolean;
  sinceLastEntryOnly?: boolean;
};

type RruleEntryIndex = {
  skippedDates: Set<string>;
  completedDates: Set<string>;
  completedEntryDates: Map<string, Timestamp>;
  lastEntryDate?: Timestamp;
};

// Range-independent: nothing here reads startDate/endDate/today, so one pass per schedule+flags
// serves every date window the caller asks for. Returned collections are shared — never mutate them.
const getRruleEntryIndex = memoizeByScheduleAndVariant<
  RruleEntryIndexOptions,
  RruleEntryIndex
>(
  ({ uncompletedOnly, sinceLastEntryOnly }) =>
    `${uncompletedOnly ? 1 : 0}:${sinceLastEntryOnly ? 1 : 0}`,
  (schedule, { uncompletedOnly, sinceLastEntryOnly }) =>
    schedule.entries.reduce<RruleEntryIndex>(
      (acc, entry) => {
        if (
          entry.state === ScheduleEntryState.Skip ||
          entry.state === ScheduleEntryState.Failure
        ) {
          acc.skippedDates.add(formatDateKey(entry.date));
        }

        if (
          entry.state === ScheduleEntryState.Complete &&
          (entry.value ?? 0) >= schedule.completionTarget.count
        ) {
          acc.completedEntryDates.set(formatDateKey(entry.date), entry.date);

          if (uncompletedOnly) {
            acc.completedDates.add(formatDateKey(entry.date));
          }
        }

        if (
          sinceLastEntryOnly &&
          ((entry.state === ScheduleEntryState.Complete &&
            (entry.value ?? 0) >= schedule.completionTarget.count) ||
            entry.state !== ScheduleEntryState.Complete)
        ) {
          acc.lastEntryDate =
            !acc.lastEntryDate ||
            acc.lastEntryDate.getTime() < entry.date.getTime()
              ? entry.date
              : acc.lastEntryDate;
        }

        return acc;
      },
      {
        skippedDates: new Set(),
        completedDates: new Set(),
        completedEntryDates: new Map(),
        lastEntryDate: undefined,
      },
    ),
);

export const getRrulePlanningBySchedule = (
  schedule: HabitSchedule,
  {
    startDate,
    endDate,
    weekStartsOn,
    uncompletedOnly,
    sinceLastEntryOnly,
    strictEndDate,
  }: Options,
) => {
  // figure out where to start from
  const scheduleStartDate = startOfDay(schedule.startDate);
  const scheduleEnd = getScheduleEndDate(schedule);

  const { skippedDates, completedDates, completedEntryDates, lastEntryDate } =
    getRruleEntryIndex(schedule, { uncompletedOnly, sinceLastEntryOnly });

  const _start = new Date(
    Math.max(
      // get the later date of the two; given startDate or schedule start date
      startDate.getTime(),
      scheduleStartDate.getTime(),
      lastEntryDate?.getTime() ?? startDate.getTime(),
    ),
  );
  // figure out where to end
  const _end = new Date(
    Math.min(endDate.getTime(), scheduleEnd?.getTime() ?? endDate.getTime()),
  );

  // if there are timeframes from before the startDate (entries entered before the start of the schedule) we also take those
  const preTimeframes =
    _start.getTime() < scheduleStartDate.getTime()
      ? getScheduleTimeframesByDateRange(schedule, {
          startDate: _start,
          endDate: endOfDay(addDays(scheduleStartDate, -1)),
          weekStartsOn,
        })
      : [];

  // From the schedule's own start this is O(age of habit); occurrences are clamped to startDate anyway.
  const duringTimeframes = getScheduleTimeframesByDateRange(schedule, {
    startDate: new Date(
      Math.max(scheduleStartDate.getTime(), startOfDay(startDate).getTime()),
    ),
    endDate: _end,
    weekStartsOn,
  });

  // get a list of all unique timeframes
  const timeframes = [
    ...new Map(
      [...preTimeframes, ...duringTimeframes].map((t) => [
        `${t.startDate.getTime()}:${t.endDate.getTime()}`,
        t,
      ]),
    ).values(),
  ];

  // get rrule occurrences
  const rruleOccurrences = getOccurrences([schedule], {
    startDate: _start,
    endDate: _end,
  });

  // loop over timeframes
  return timeframes.reduce<PlannedOccurrence[]>((acc, timeframe) => {
    // strictEndDate clamps to the query end; it never widens a timeframe past its own end
    const timeframeEndDate =
      strictEndDate && isAfter(timeframe.endDate, endDate)
        ? endDate
        : timeframe.endDate;
    const plannedOccurrences =
      schedule.plannedOccurrences
        ?.filter(({ date }) =>
          isBetween(date, timeframe.startDate, timeframeEndDate),
        )
        .sort(
          (plannedA, plannedB) =>
            plannedA.date.getTime() - plannedB.date.getTime(),
        ) ?? [];

    if (plannedOccurrences.length) {
      // completed entries surface on their own day even in planned timeframes
      const completedOccurrences = uncompletedOnly
        ? []
        : [...completedEntryDates.values()]
            .filter(
              (date) =>
                !skippedDates.has(formatDateKey(date)) &&
                isBetween(date, timeframe.startDate, timeframeEndDate) &&
                (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
                !isBefore(date, startDate) &&
                !isAfter(date, _end) &&
                !plannedOccurrences.some((planned) =>
                  isSameDay(planned.date, date),
                ),
            )
            .map((date) => ({
              id: uuidv4(),
              date: endOfDay(date),
              timeslot: schedule.timeslot ?? null,
            }));

      acc.push(
        ...plannedOccurrences.filter(({ date }) => {
          const dateKey = formatDateKey(date);
          return (
            !skippedDates.has(dateKey) &&
            !completedDates.has(dateKey) &&
            (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
            // occurrences from before the start date are not wanted
            !isBefore(date, startDate) &&
            // occurrences after the end date are not wanted
            !isAfter(date, _end)
          );
        }),
        ...completedOccurrences,
      );

      return acc;
    }

    // get the occurrences between start- and end-date and map those to plannedOccurrences
    for (const date of rruleOccurrences) {
      const dateKey = formatDateKey(date);
      if (
        !skippedDates.has(dateKey) &&
        !completedDates.has(dateKey) &&
        (!lastEntryDate || !isBefore(date, lastEntryDate)) &&
        isBetween(date, timeframe.startDate, timeframeEndDate) &&
        // occurrences from before the start date are not wanted
        !isBefore(date, startDate) &&
        // occurrences after the end date are not wanted
        !isAfter(date, _end)
      ) {
        acc.push({ id: uuidv4(), date, timeslot: schedule.timeslot ?? null });
      }
    }

    return acc;
  }, []);
};
